// First Time mode (Settings > Game, on by default): friendly step-by-step tips
// for new players. At a table a short tour explains poker, spells and how a
// game goes; later, small tips show at the right moment (first spellbook
// open, first aim, first turn, first hand result). The part of the screen a
// tip talks about gets a glowing ring. Seen tips are remembered on this
// device (settings/tipsSeen.ts); "Show tips again" in Settings resets them.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { BANKRUPT_REFILL_COINS, SPELLS_BY_ID } from "@wp/shared";
import { formatCoins } from "../../lib/format";
import { COMPACT_QUERY, useMediaQuery } from "../../lib/useMediaQuery";
import { useSettings } from "../../settings/SettingsContext";
import { markTipSeen, useTipsSeen } from "../../settings/tipsSeen";

interface TipStep {
  title: string;
  body: ReactNode;
  /** CSS selector of the part of the screen this tip is about: it glows while the tip shows. */
  target?: string;
}

const TURN_OFF = (
  <>
    <strong>Settings</strong> (gear icon, top right) &gt; <strong>Game</strong> &gt; <strong>First Time mode</strong>
  </>
);

const SEL = {
  hole: '[data-testid="hole-cards"]',
  actions: ".action-grid",
  stats: '[data-testid="my-health"], [data-testid="my-juice"]',
  book: '[data-testid="spellbook-button"], [data-testid="spellbook-panel"]',
  start: '[data-testid="host-start"]',
  settings: '[data-testid="settings-button"]',
  aim: '[data-testid="aim-hint"]',
  result: '[data-testid="result-banner"]',
};

// ---------- shared bits of tip text ----------

function Moves() {
  return (
    <ul className="ft-list">
      <li>
        <strong>Check</strong>: stay in without betting (only when nobody has bet yet).
      </li>
      <li>
        <strong>Call</strong>: put in the same amount as the bet to stay in.
      </li>
      <li>
        <strong>Raise</strong>: bet more, so others must match you.
      </li>
      <li>
        <strong>All In</strong>: bet every coin you have.
      </li>
      <li>
        <strong>Fold</strong>: give up this hand (what you bet stays in the pot).
      </li>
    </ul>
  );
}

const RANKS: [string, string][] = [
  ["Royal Flush", "10, J, Q, K, A, all one suit"],
  ["Straight Flush", "5 in a row, all one suit"],
  ["Four of a Kind", "4 cards of the same rank"],
  ["Full House", "3 of a kind + a pair"],
  ["Flush", "5 cards of one suit"],
  ["Straight", "5 in a row, any suits"],
  ["Three of a Kind", "3 cards of the same rank"],
  ["Two Pair", "2 different pairs"],
  ["Pair", "2 cards of the same rank"],
  ["High Card", "none of the above: highest card counts"],
];

/** The five spells new players should try first (cost and casts per hand from the spell list). */
const TRY_SPELLS: { id: string; what: string; tryThis: string }[] = [
  { id: "blaster", what: "Shoots one player for 10 damage.", tryThis: "aim it at another wizard." },
  { id: "card_illusion", what: "Turns any one card on the table into a card you pick.", tryThis: "turn one of your own cards into an Ace." },
  { id: "barrier", what: "Blocks the next 3 spells other players aim at you.", tryThis: "cast it on yourself early in a hand." },
  {
    id: "cleanse",
    what: "Aim at a card to remove traps and changes on it, or at yourself to remove bad effects like being frozen or bugs.",
    tryThis: "frozen? Cleanse yourself (it works even while frozen).",
  },
  {
    id: "meteor_swarm",
    what: "Hits every player (you too) for 35 damage and swaps the table cards for new ones. Not during Pre-flop or Showdown.",
    tryThis: "it costs all of your 100 juice, so save it for a big moment (Overcharge can add extra juice first)."
  },
];

function spellCost(id: string): string {
  const def = SPELLS_BY_ID.get(id);
  if (!def) return "";
  const juice = typeof def.manaCost === "number" ? def.manaCost : `${def.manaCost.min}-${def.manaCost.max}`;
  const times = def.castsPerHand === 1 ? "once a hand" : `${def.castsPerHand}× a hand`;
  return `${juice} juice · ${times}`;
}

function TrySpells() {
  return (
    <ul className="ft-list ft-spells">
      {TRY_SPELLS.map((s) => (
        <li key={s.id}>
          <strong>{SPELLS_BY_ID.get(s.id)?.name ?? s.id}</strong> <span className="ft-cost">{spellCost(s.id)}</span>
          <br />
          {s.what} <em>Try this:</em> {s.tryThis}
        </li>
      ))}
    </ul>
  );
}

function CastSteps() {
  return (
    <ol className="ft-list">
      <li>
        Click a spell in the book, then <strong>Quick Assemble</strong> (before the game: <strong>Practice</strong>).
      </li>
      <li>Whatever it can hit glows red: click a player or a card to lock it. Area spells: click anywhere on the table.</li>
      <li>
        <strong>Cast</strong> fires it now. <strong>Queue</strong> saves it for later; <strong>Execute</strong> fires everything you queued.
      </li>
    </ol>
  );
}

// ---------- the tours ----------

function welcomeTour(ante: number): TipStep[] {
  return [
    {
      title: "Welcome to Wizard Poker!",
      body: (
        <p>
          It&apos;s Texas Hold&apos;em poker, plus magic spells. These tips show you how it all works. Press <strong>Next</strong>, or{" "}
          <strong>Skip</strong> if you already know how to play.
        </p>
      ),
    },
    {
      title: "Your cards and the table cards",
      body: (
        <p>
          You get <strong>2 cards</strong> only you can see (bottom left, click them to look). <strong>5 cards</strong> are put face up in
          the middle for everyone to share. Your hand is the <strong>best 5 cards</strong> out of your 2 plus those 5.
        </p>
      ),
      target: SEL.hole,
    },
    {
      title: "Four betting rounds",
      body: (
        <>
          <ul className="ft-list">
            <li>
              <strong>Pre-flop</strong>: just your 2 cards.
            </li>
            <li>
              <strong>Flop</strong>: 3 table cards appear.
            </li>
            <li>
              <strong>Turn</strong>: 1 more card.
            </li>
            <li>
              <strong>River</strong>: the last card.
            </li>
          </ul>
          <p>
            Then <strong>Showdown</strong>: the best hand wins the <strong>pot</strong> (all the coins bet). If everyone else folds, you win
            without showing.
          </p>
        </>
      ),
    },
    {
      title: "Your moves",
      body: (
        <>
          <p>When it&apos;s your turn, the buttons at the bottom light up:</p>
          <Moves />
          <p className="ft-small">The arrow button switches between Check / Call / Raise and All In / Fold.</p>
        </>
      ),
      target: SEL.actions,
    },
    {
      title: "The Min",
      body: (
        <p>
          Every hand, each seated player pays the <strong>Min</strong> (the minimum bet) just to get cards. It starts the pot. At this
          table the Min is <strong>{formatCoins(ante)} coins</strong>.
        </p>
      ),
    },
    {
      title: "Which hand wins",
      body: (
        <>
          <p className="ft-small">Best first:</p>
          <ol className="ft-list ft-ranks">
            {RANKS.map(([name, what]) => (
              <li key={name}>
                <strong>{name}</strong>: {what}
              </li>
            ))}
          </ol>
        </>
      ),
    },
    {
      title: "Health and juice",
      body: (
        <p>
          Red is your <strong>health</strong>, blue is your <strong>juice</strong> (your magic power). Both start at 100 and refill at the
          start of every hand. Spells cost juice. At 0 health you are <strong>knocked out</strong> and fold that hand.
        </p>
      ),
      target: SEL.stats,
    },
    {
      title: "Your spellbook",
      body: (
        <>
          <p>The book at the top right holds the spells, in 3 groups:</p>
          <ul className="ft-list">
            <li>
              <strong>Direct</strong>: hits one player.
            </li>
            <li>
              <strong>Trap</strong>: hidden on a card. Only you know which and where.
            </li>
            <li>
              <strong>Area</strong>: hits the whole table.
            </li>
          </ul>
          <p className="ft-small">Each spell shows its juice cost and how many times you can use it per hand.</p>
        </>
      ),
      target: SEL.book,
    },
    {
      title: "Casting a spell",
      body: (
        <>
          <CastSteps />
          <p className="ft-small">You can cast any time during a hand, even when it isn&apos;t your turn. Esc or × cancels.</p>
        </>
      ),
      target: SEL.book,
    },
    { title: "Five spells to try", body: <TrySpells />, target: SEL.book },
    {
      title: "How a game starts",
      body: (
        <p>
          Click an <strong>open seat</strong> at the table to sit down. Before the game there is a <strong>practice hand</strong>: spells
          are free and nothing is bet, so try them out! When everyone is ready, the <strong>host</strong> presses{" "}
          <strong>Start session</strong>.
        </p>
      ),
      target: SEL.start,
    },
    {
      title: "Every hand",
      body: (
        <>
          <ol className="ft-list">
            <li>Everyone pays the Min and gets 2 cards.</li>
            <li>Bet and cast spells while the table cards come out.</li>
            <li>Showdown: the best hand takes the pot.</li>
            <li>Health and juice refill for the next hand.</li>
          </ol>
          <p className="ft-small">
            Coins are your <strong>real account coins</strong>: bets come out of them and wins go back in. Out of coins? You get{" "}
            {formatCoins(BANKRUPT_REFILL_COINS)} back to keep playing.
          </p>
        </>
      ),
    },
    {
      title: "End of the session",
      body: (
        <p>
          The session ends when the hand limit is reached or the host ends it. Whoever won the <strong>most hands</strong> wins the
          session (bragging rights only, no extra coins).
        </p>
      ),
    },
    {
      title: "Turning tips off",
      body: (
        <p>
          These tips show while First Time mode is on. To turn them off: {TURN_OFF}. The full rules are in Settings &gt;{" "}
          <strong>How to Play</strong>. Have fun!
        </p>
      ),
      target: SEL.settings,
    },
  ];
}

const SPELLBOOK_TOUR: TipStep[] = [
  {
    title: "Your spellbook",
    body: (
      <>
        <p>
          Spells come in 3 groups: <strong>Direct</strong> (one player), <strong>Trap</strong> (hidden on a card) and <strong>Area</strong>{" "}
          (whole table). Hover or long-press a spell to read what it does.
        </p>
        <CastSteps />
      </>
    ),
    target: SEL.book,
  },
  { title: "Good first spells", body: <TrySpells />, target: SEL.book },
];

const AIM_TOUR: TipStep[] = [
  {
    title: "Pick a target",
    body: (
      <p>
        What this spell can hit glows red. Click one to lock it (area spells: click anywhere on the table). Then <strong>Cast</strong> fires
        it now, or <strong>Queue</strong> saves it for later. Esc, a right-click or × cancels.
      </p>
    ),
    target: SEL.aim,
  },
];

const TURN_TOUR: TipStep[] = [
  {
    title: "It's your turn!",
    body: (
      <>
        <Moves />
        <p className="ft-small">Not sure? Check if you can, or Call to stay in. The timer shows how long you have.</p>
      </>
    ),
    target: SEL.actions,
  },
];

const RESULT_TOUR: TipStep[] = [
  {
    title: "Hand over",
    body: (
      <p>
        The best hand (or the last player still in) takes the pot, straight into their account. Next hand everyone pays the Min again,
        and health and juice refill.
      </p>
    ),
    target: SEL.result,
  },
];

// ---------- the panel ----------

/** Adds a glowing ring to the elements matching `selector` while mounted (they may come and go). */
function useGlow(selector: string | undefined) {
  useEffect(() => {
    if (!selector) return;
    let marked: Element[] = [];
    const apply = () => {
      let now: Element[] = [];
      try {
        now = [...document.querySelectorAll(selector)];
      } catch {
        return;
      }
      for (const el of marked) if (!now.includes(el)) el.classList.remove("ft-glow");
      for (const el of now) el.classList.add("ft-glow");
      marked = now;
    };
    apply();
    const id = window.setInterval(apply, 400);
    return () => {
      window.clearInterval(id);
      for (const el of marked) el.classList.remove("ft-glow");
    };
  }, [selector]);
}

function TipsPanel({ steps, onDone, className = "" }: { steps: TipStep[]; onDone: () => void; className?: string }) {
  const [i, setI] = useState(0);
  const step = steps[Math.min(i, steps.length - 1)]!;
  const last = i >= steps.length - 1;
  useGlow(step.target);
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => bodyRef.current?.scrollTo?.({ top: 0 }), [i]);
  return (
    <aside className={`ft-panel window ${className}`} role="dialog" aria-label="Tips for new players" data-testid="first-time-tips">
      <header className="ft-head">
        <span className="ft-count" data-testid="ft-count">
          {steps.length > 1 ? `Tip ${i + 1} of ${steps.length}` : "Tip"}
        </span>
        <button type="button" className="ft-close" onClick={onDone} aria-label="Close tips" title="Close tips">
          ×
        </button>
      </header>
      <h3 className="ft-title">{step.title}</h3>
      <div className="ft-body" ref={bodyRef}>
        {step.body}
      </div>
      <footer className="ft-actions">
        {steps.length > 1 && !last ? (
          <button type="button" className="btn small link ft-skip" onClick={onDone} data-testid="ft-skip">
            Skip
          </button>
        ) : (
          <span />
        )}
        <span className="ft-nav">
          {steps.length > 1 && (
            <button type="button" className="btn small" disabled={i === 0} onClick={() => setI((n) => Math.max(0, n - 1))} data-testid="ft-back">
              Back
            </button>
          )}
          <button
            type="button"
            className="btn small primary"
            onClick={() => (last ? onDone() : setI((n) => n + 1))}
            data-testid="ft-next"
          >
            {last ? "Got it" : "Next"}
          </button>
        </span>
      </footer>
    </aside>
  );
}

// ---------- at the table ----------

export interface TableMoment {
  /** The open spellbook. */
  spellbookOpen: boolean;
  /** Aiming a spell (before the Cast / Queue pop-up). */
  aiming: boolean;
  /** Your turn to bet in a real hand. */
  myTurn: boolean;
  /** A real hand just ended (its result is up). */
  handOver: boolean;
  /** The table's Min. */
  ante: number;
}

type TourId = "welcome" | "spellbook" | "aim" | "turn" | "result";
/** Context tips, most useful first; each shows once, when its moment comes. */
const MOMENTS: { id: Exclude<TourId, "welcome">; when: (m: TableMoment) => boolean }[] = [
  { id: "aim", when: (m) => m.aiming },
  { id: "turn", when: (m) => m.myTurn },
  { id: "spellbook", when: (m) => m.spellbookOpen },
  { id: "result", when: (m) => m.handOver },
];
const tipId = (t: TourId) => `table.${t}`;
/** A context tip that goes away on its own (its moment passed) counts as seen once it was up this long. */
const READ_MS = 4000;

/** First Time mode tips at a game table (nothing when the setting is off). */
export function TableTips(moment: TableMoment) {
  const { settings } = useSettings();
  const seen = useTipsSeen();
  const on = settings.game.firstTime;
  const [active, setActive] = useState<{ id: TourId; at: number } | null>(null);
  // Phones and small windows (Discord): the tip floats over the HUD, so the open spellbook
  // (which fills most of a small screen) cannot hide it; CSS places it out of the table's way.
  const float = useMediaQuery(COMPACT_QUERY);

  // Pick the next tip: the welcome tour first, then whichever moment is happening.
  const next: TourId | null = !on
    ? null
    : !seen.has(tipId("welcome"))
      ? "welcome"
      : (MOMENTS.find((m) => !seen.has(tipId(m.id)) && m.when(moment))?.id ?? null);
  useEffect(() => {
    if (!on) setActive(null);
    else if (!active && next) setActive({ id: next, at: Date.now() });
  }, [on, active, next]);

  // A context tip whose moment passed goes away (seen if it was up long enough).
  const moment_ = MOMENTS.find((m) => m.id === active?.id);
  const stillNow = moment_ ? moment_.when(moment) : true;
  useEffect(() => {
    if (!active || stillNow) return;
    if (Date.now() - active.at >= READ_MS) markTipSeen(tipId(active.id));
    setActive(null);
  }, [active, stillNow]);

  const steps = useMemo(() => {
    switch (active?.id) {
      case "welcome":
        return welcomeTour(moment.ante);
      case "spellbook":
        return SPELLBOOK_TOUR;
      case "aim":
        return AIM_TOUR;
      case "turn":
        return TURN_TOUR;
      case "result":
        return RESULT_TOUR;
      default:
        return null;
    }
  }, [active?.id, moment.ante]);

  if (!on || !active || !steps) return null;
  const panel = (
    <TipsPanel
      key={active.id}
      steps={steps}
      className={float ? "ft-table ft-float" : "ft-table"}
      onDone={() => {
        markTipSeen(tipId(active.id));
        setActive(null);
      }}
    />
  );
  return float ? createPortal(panel, document.body) : panel;
}

// ---------- main menu ----------

const MENU_TIP: TipStep[] = [
  {
    title: "New to Wizard Poker?",
    body: (
      <p>
        Join an open game or create one. At the table, tips will walk you through poker and spells. To turn tips off: {TURN_OFF}.
      </p>
    ),
  },
];

/** A short First Time mode welcome on the main menu (once). */
export function MenuTip() {
  const { settings } = useSettings();
  const seen = useTipsSeen();
  if (!settings.game.firstTime || seen.has("menu.welcome")) return null;
  return <TipsPanel steps={MENU_TIP} className="ft-menu" onDone={() => markTipSeen("menu.welcome")} />;
}
